import { z } from 'zod';
/**
 * THE GUIDED-ONBOARDING WIZARD'S WHOLE PROGRESS, IN ONE ROUND TRIP — S37 Slice A.
 *
 * WHY ONE DTO AND NOT FIVE QUERIES. The wizard (`apps/visitor` `/admin/onboarding`) is the
 * one screen in the product that POLLS: steps 4 and 5 wait for a kiosk to pair and for the
 * first visitor to arrive, neither of which the browser causes. Five separate list endpoints
 * polled in parallel would be five round trips per tick, five sets of pagination meta the
 * wizard throws away, and five permissions to hold; this is one narrow read behind one
 * permission, which is what makes a `refetchInterval` defensible at all
 * (docs/plans/s37-guided-onboarding.md, "Non-negotiables that bite here").
 *
 * EVERY FIELD IS DERIVED, NOTHING IS STORED. There is no `onboardingStep` column and there
 * must not be: the same reasoning `platform/tenant/readiness.ts` records — a wizard abandoned
 * halfway leaves no flag to repair, and a tenant that deletes its last location is walked
 * through setup again rather than keeping a green tick over a product that cannot check
 * anyone in.
 *
 * NOT THE SAME SIGNAL AS `MeDTO.onboardingRequired`, deliberately. That one is the hard
 * redirect and is gated on `hasPairedDevice` alone; `hasFirstCheckIn` here drives the finale
 * and a resumable nudge. Gating the redirect on the check-in would brick billing and
 * memberships for any tenant that never checks anyone in — a lockout, not a funnel.
 */
export const onboardingStatusDTOSchema = z.object({
    /**
     * The tenant's HQ — the location auto-created at signup as `<Tenant> HQ`, and after that
     * simply the tenant's OLDEST location. `null` only for a tenant whose locations have all
     * been deleted, which is a real state the wizard renders as "create a site first" rather
     * than a 404.
     */
    hqLocationId: z.string().min(1).nullable(),
    hqLocationName: z.string().min(1).nullable(),
    /** The IANA zone that location was born with — the browser's, when signup sent one. */
    hqTimezone: z.string().min(1).nullable(),
    /**
     * Step 1. `true` once HQ's `KioskBranding` row carries an accent colour OR a logo — either
     * alone is a deliberate choice an admin made, and requiring both would leave a tenant that
     * only has a wordmark stuck on a step it had finished.
     */
    brandingConfigured: z.boolean(),
    /** Step 2. `Person` rows with `personType: 'EMPLOYEE'`. A count, not a boolean, so the
     *  wizard can say "3 added" rather than only "done". */
    employeeContactCount: z.number().int().nonnegative(),
    /** Step 4, and the same fact `MeDTO.onboardingRequired` is the negation of. */
    hasPairedDevice: z.boolean(),
    /** Step 5. Ever — not today, not this week. A tenant does not un-onboard. */
    hasFirstCheckIn: z.boolean(),
});
//# sourceMappingURL=onboarding.js.map